"use client";

import { Gear, Palette, SignOut } from "@phosphor-icons/react";
import { useRouter } from "next/navigation";
import { Avatar, AvatarFallback } from "@/shared/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { FOCUS } from "@/shared/ui/law";
import { cn } from "@/shared/lib/utils";
import { useAuthContext } from "./AuthProvider";

/**
 * UserMenu — the account affordance at the header's far right.
 *
 * The trigger is an `Avatar` (initials on the accent wash) rather than a
 * tinted ghost button, and the menu leads with an identity block so the
 * signed-in user is legible before any action. Every row here navigates or
 * acts: "Profile" and "Settings" used to be items with no handler, which is
 * a control that lies. Appearance opens the palette / face / density panel;
 * Admin opens the control plane.
 */
export function UserMenu() {
  const { user, isAuthenticated, logout } = useAuthContext();
  const router = useRouter();

  if (!isAuthenticated || !user) return null;

  const initials = user.display_name
    ? user.display_name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : user.username.slice(0, 2).toUpperCase();

  async function handleLogout() {
    await logout();
    router.push("/login");
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Account"
          className={cn(
            "rounded-full transition-tint hover:opacity-90 data-[state=open]:shadow-ring",
            FOCUS,
          )}
        >
          <Avatar className="size-7">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-60">
        <div className="flex items-center gap-2.5 px-2 py-2">
          <Avatar className="size-8">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{user.display_name || user.username}</p>
            <p className="truncate text-xs text-muted-foreground">{user.email}</p>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => router.push("/admin/appearance")}>
          <Palette />
          Appearance
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => router.push("/admin")}>
          <Gear />
          Admin
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onClick={handleLogout}>
          <SignOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
